import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CardContainer } from '../components/ui/CardContainer';
import { CircleNotchIcon, CheckCircleIcon, XCircleIcon } from '@phosphor-icons/react';
import { env } from '../config/env';
import { AuthLayout } from '../components/layout/AuthLayout';

export const EmailVerificationPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setErrorMessage(t('auth.email_verification.error.missing_token'));
        return;
      }

      try {
        const response = await fetch(`${env.VITE_API_URL}/auth/verify-email?token=${token}`, {
          method: 'GET',
          credentials: 'include',
        });

        if (response.ok) {
          setStatus('success');
          setTimeout(() => {
            navigate('/account-confirmed');
          }, 2000);
        } else {
          const data = await response.json();
          setStatus('error');
          setErrorMessage(data.message || t('auth.email_verification.error.verification_failed'));
        }
      } catch (error) {
        setStatus('error');
        setErrorMessage(t('auth.email_verification.error.connection_error'));
      }
    };

    verifyEmail();
  }, [searchParams, navigate, t]);

  return (
    <AuthLayout>
      <CardContainer>
        <div className="text-center">
          {status === 'loading' && (
            <>
              <CircleNotchIcon
                size={64}
                className="mx-auto mb-4 text-primary animate-spin"
                weight="bold"
              />
              <h1 className="text-2xl font-display text-primary mb-2">
                {t('auth.email_verification.loading')}
              </h1>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircleIcon size={64} className="mx-auto mb-4 text-green-500" weight="fill" />
              <h1 className="text-2xl font-display text-primary mb-2">
                {t('auth.email_verification.success.title')}
              </h1>
              <p className="text-secondary">{t('auth.email_verification.success.description')}</p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircleIcon size={64} className="mx-auto mb-4 text-red-500" weight="fill" />
              <h1 className="text-2xl font-display text-primary mb-2">
                {t('auth.email_verification.error.title')}
              </h1>
              <p className="text-red-500 mb-4">{errorMessage}</p>
            </>
          )}
        </div>
      </CardContainer>
    </AuthLayout>
  );
};
